/**
 * Advanced Course Discovery Page
 * Features: Search, Filter, Sort, and Pagination
 */

import React, { useState, useEffect } from 'react';
import { coursesAPI, analyticsAPI } from '../services/api';
import { searchAndFilterCourses, courseFilters } from '../services/searchService';
import LoadingSpinner from '../components/LoadingSpinner';
import logger from '../utils/logger';

const CourseDiscovery = () => {
  const [courses, setCourses] = useState([]);
  const [trending, setTrending] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [difficulty, setDifficulty] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [minRating, setMinRating] = useState(0);
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Load courses on mount
  useEffect(() => {
    loadCourses();
  }, []);

  // Apply filters and sorting
  useEffect(() => {
    applyFiltersAndSort();
  }, [courses, searchTerm, difficulty, priceRange, minRating, category, sortBy]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const [coursesRes, trendingRes] = await Promise.all([
        coursesAPI.getAll(),
        analyticsAPI.getTrendingCourses(5)
      ]);

      if (coursesRes.data.success) {
        setCourses(coursesRes.data.courses || []);
      }
      if (trendingRes.data.success) {
        setTrending(trendingRes.data.trending || []);
      }
    } catch (err) {
      logger.error('Error loading courses:', err);
      setError(err.userMessage || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    const result = searchAndFilterCourses(courses, {
      searchTerm,
      difficulty: difficulty !== 'all' ? difficulty : undefined,
      priceRange: priceRange !== 'all' ? priceRange : undefined,
      minRating,
      category: category !== 'all' ? category : undefined,
      sortBy,
      page: 1,
      itemsPerPage
    });

    setFilteredCourses(result.courses);
    setCurrentPage(1);
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '30px'
    },
    title: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#64748b'
    },
    searchBar: {
      width: '100%',
      padding: '12px 16px',
      fontSize: '1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      marginBottom: '20px',
      outline: 'none',
      transition: 'border-color 0.3s'
    },
    filterSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px',
      marginBottom: '20px',
      padding: '20px',
      background: '#f8fafc',
      borderRadius: '8px'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    filterLabel: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#1e293b'
    },
    select: {
      padding: '8px 12px',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      fontSize: '0.9rem',
      background: 'white',
      cursor: 'pointer'
    },
    courseGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '20px',
      marginTop: '20px'
    },
    courseCard: {
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s'
    },
    courseImage: {
      width: '100%',
      height: '160px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '3rem',
      color: 'white'
    },
    courseContent: {
      padding: '16px'
    },
    courseTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    courseDescription: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '12px',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    },
    courseMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.85rem',
      color: '#64748b',
      borderTop: '1px solid #e2e8f0',
      paddingTop: '12px'
    },
    coursePrice: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#27b05b'
    },
    trendingSection: {
      marginBottom: '40px'
    },
    sectionTitle: {
      fontSize: '1.3rem',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '15px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#64748b'
    },
    emptyIcon: {
      fontSize: '3rem',
      marginBottom: '10px'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      gap: '10px',
      marginTop: '30px'
    },
    paginationBtn: {
      padding: '8px 12px',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      background: 'white',
      cursor: 'pointer',
      fontSize: '0.9rem',
      transition: 'all 0.2s'
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading courses..." />;
  }

  const totalPages = Math.ceil(filteredCourses.length / itemsPerPage);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🎓 Discover Courses</h1>
        <p style={styles.subtitle}>Find and enroll in courses that match your interests</p>
      </div>

      {/* Trending Section */}
      {trending.length > 0 && (
        <div style={styles.trendingSection}>
          <h2 style={styles.sectionTitle}>🔥 Trending Now</h2>
          <div style={styles.courseGrid}>
            {trending.map(course => (
              <div
                key={course.id}
                style={{ ...styles.courseCard }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={styles.courseImage}>📚</div>
                <div style={styles.courseContent}>
                  <div style={styles.courseTitle}>{course.title}</div>
                  <div style={styles.courseDescription}>{course.description}</div>
                  <div style={styles.courseMeta}>
                    <span>{course.lessons_count || 0} lessons</span>
                    <span style={styles.coursePrice}>${course.price || '0'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Bar */}
      <input
        type="text"
        placeholder="Search courses by title or topic..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={styles.searchBar}
        onFocus={(e) => e.target.style.borderColor = '#27b05b'}
        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
      />

      {/* Filters */}
      <div style={styles.filterSection}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>🎯 Difficulty</label>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Levels</option>
            {courseFilters.Difficulty.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>💰 Price Range</label>
          <select
            value={priceRange}
            onChange={(e) => setPriceRange(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Prices</option>
            {courseFilters.PriceRanges.map(p => (
              <option key={p.label} value={p.label}>{p.label}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>⭐ Minimum Rating</label>
          <select
            value={minRating}
            onChange={(e) => setMinRating(Number(e.target.value))}
            style={styles.select}
          >
            <option value={0}>All Ratings</option>
            {courseFilters.Ratings.map(r => (
              <option key={r} value={r}>{r}+ stars</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>📂 Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Categories</option>
            <option value="english">English</option>
            <option value="grammar">Grammar</option>
            <option value="pronunciation">Pronunciation</option>
            <option value="business">Business</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>📊 Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={styles.select}
          >
            {courseFilters.SortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div style={{ marginTop: '20px', color: '#64748b', fontSize: '0.95rem' }}>
        Found <strong>{filteredCourses.length}</strong> course{filteredCourses.length !== 1 ? 's' : ''}
      </div>

      {/* Courses Grid */}
      {filteredCourses.length > 0 ? (
        <>
          <div style={styles.courseGrid}>
            {filteredCourses
              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
              .map(course => (
                <div
                  key={course.id}
                  style={{ ...styles.courseCard }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={styles.courseImage}>📚</div>
                  <div style={styles.courseContent}>
                    <div style={styles.courseTitle}>{course.title}</div>
                    <div style={styles.courseDescription}>{course.description}</div>
                    <div style={styles.courseMeta}>
                      <span>{course.lessons_count || 0} lessons</span>
                      <span style={styles.coursePrice}>${course.price || '0'}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    ...styles.paginationBtn,
                    background: page === currentPage ? '#27b05b' : 'white',
                    color: page === currentPage ? 'white' : '#1e293b',
                    borderColor: page === currentPage ? '#27b05b' : '#cbd5e1'
                  }}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🔍</div>
          <h3>No courses found</h3>
          <p>Try adjusting your filters or search term</p>
        </div>
      )}
    </div>
  );
};

export default CourseDiscovery;
